import React, { useState, useRef, useEffect } from 'react';
import { TrendingUp, TrendingDown, AlertTriangle, Search, Send, Zap, CheckCircle, Clock, Eye, Loader, Paperclip, Mic, ArrowLeft, Bot, Megaphone, Radio, ChevronDown, ChevronUp, Target, BarChart3, ArrowUpRight, Shield, Check, ClipboardList } from 'lucide-react';
import { AGENT_THUMBS } from './V4BriefingShared';
import { MARKETING_DEPT_DATA, MARKETING_CHAT_RESPONSES, MARKETING_DEEP_DIVE, MARKETING_DECISION_DEEP_DIVES, MARKETING_ALERT_DEEP_DIVES, MARKETING_CAMPAIGN_DEEP_DIVES } from '../../data/marketingDeptData';
import { useRegisterChatInput } from '../../hooks/useChatInputRegistry';
import { scaleDelay } from '../../hooks/demoSpeed';

const AGENT_NAMES = { stark: 'Stark', luc: 'Luc', marsi: 'Marsi', sally: 'Sally', zira: 'Zira', tali: 'Tali', finn: 'Finn', wright: 'Wright', buzz: 'Buzz' };

const STATUS_COLOR = {
  green: { ring: '#34d399', dot: 'bg-emerald-400', text: 'text-emerald-400', lightText: 'text-emerald-600' },
  amber: { ring: '#fbbf24', dot: 'bg-amber-400', text: 'text-amber-400', lightText: 'text-amber-600' },
  red: { ring: '#f87171', dot: 'bg-red-400', text: 'text-red-400', lightText: 'text-red-500' },
};

const ACTION_STATUS = {
  'complete': { icon: CheckCircle, dark: 'text-emerald-400', light: 'text-emerald-600' },
  'in-progress': { icon: Loader, dark: 'text-blue-400', light: 'text-blue-600' },
  'pending-approval': { icon: Clock, dark: 'text-amber-400', light: 'text-amber-600' },
  'recommended': { icon: Zap, dark: 'text-indigo-400', light: 'text-indigo-600' },
  'exploring': { icon: Eye, dark: 'text-white/40', light: 'text-slate-500' },
};

const CAMPAIGN_STATUS = {
  live: { label: 'Live', dark: 'text-emerald-400 bg-emerald-400/10', light: 'text-emerald-600 bg-emerald-600/10' },
  ready: { label: 'Ready', dark: 'text-indigo-400 bg-indigo-400/10', light: 'text-indigo-600 bg-indigo-600/10' },
  paused: { label: 'Paused', dark: 'text-amber-400 bg-amber-400/10', light: 'text-amber-600 bg-amber-600/10' },
};

/* ── Score Ring ── */
const ScoreRing = ({ score, size, strokeWidth, isDark, status = 'green' }) => {
  const radius = (size - strokeWidth) / 2;
  const circumference = 2 * Math.PI * radius;
  const progress = (score / 100) * circumference;
  const s = STATUS_COLOR[status];

  return (
    <div className="relative flex items-center justify-center" style={{ width: size, height: size }}>
      <svg width={size} height={size} className="transform -rotate-90">
        <circle cx={size / 2} cy={size / 2} r={radius} fill="none" stroke={isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.06)'} strokeWidth={strokeWidth} />
        <circle cx={size / 2} cy={size / 2} r={radius} fill="none" stroke={s.ring} strokeWidth={strokeWidth} strokeLinecap="round" strokeDasharray={circumference} strokeDashoffset={circumference - progress} className="transition-all duration-700" />
      </svg>
      <span className={`absolute text-lg font-bold ${isDark ? s.text : s.lightText}`}>{score}</span>
    </div>
  );
};

/* ── Agent Status Card ── */
const AgentCard = ({ agent, isDark }) => {
  const tp = isDark ? 'text-white' : 'text-slate-800';
  const ts = isDark ? 'text-white/50' : 'text-slate-500';
  const cardBg = isDark ? 'bg-[#0d1b2a]' : 'bg-[#f0ece4]';
  const border = isDark ? 'border-white/[0.06]' : 'border-black/[0.06]';
  const thumb = AGENT_THUMBS[agent.id];

  return (
    <div className={`${cardBg} border ${border} rounded-lg px-3 py-2.5`} data-testid={`mkt-agent-${agent.id}`}>
      <div className="flex items-center gap-2 mb-1.5">
        {thumb && <img src={thumb} alt="" className="w-6 h-6 rounded-full object-cover" />}
        <div className="flex-1 min-w-0">
          <p className={`text-xs font-semibold ${tp}`}>{agent.name}</p>
          <p className={`text-[10px] ${ts}`}>{agent.role}</p>
        </div>
        <span className={`w-2 h-2 rounded-full ${agent.status === 'active' ? 'bg-emerald-400 animate-pulse' : 'bg-slate-400'}`} />
      </div>
      <p className={`text-[11px] leading-relaxed ${tp} mb-1`}>{agent.currentTask}</p>
      <p className={`text-[10px] ${ts}`}>Last: {agent.lastAction} · {agent.lastActionTime}</p>
    </div>
  );
};

/* ── Alert Card ── */
const AlertCard = ({ alert, isDark, onDeepDive, onEscalate, escalated, addressed }) => {
  const [expanded, setExpanded] = useState(false);
  const isHigh = alert.severity === 'high';
  const cardBg = addressed
    ? (isDark ? 'bg-emerald-950/15' : 'bg-emerald-50/50')
    : isHigh ? (isDark ? 'bg-red-950/20' : 'bg-red-50/60') : (isDark ? 'bg-[#0d1b2a]' : 'bg-[#f0ece4]');
  const border = addressed
    ? (isDark ? 'border-emerald-500/15' : 'border-emerald-200/60')
    : isHigh ? (isDark ? 'border-red-500/15' : 'border-red-200/60') : (isDark ? 'border-white/[0.06]' : 'border-black/[0.06]');
  const tp = isDark ? 'text-white' : 'text-slate-800';
  const ts = isDark ? 'text-white/50' : 'text-slate-500';
  const hoverBg = isDark ? 'hover:bg-white/[0.03]' : 'hover:bg-black/[0.02]';
  const sevColor = isHigh ? (isDark ? 'text-red-400 bg-red-400/10' : 'text-red-600 bg-red-600/10') : (isDark ? 'text-amber-400 bg-amber-400/10' : 'text-amber-700 bg-amber-700/10');

  return (
    <div className={`${cardBg} border ${border} rounded-lg px-3 py-2.5`} data-testid={`mkt-alert-${alert.id}`}>
      <div className="flex items-center gap-2">
        <AlertTriangle className={`w-3.5 h-3.5 flex-shrink-0 ${addressed ? (isDark ? 'text-emerald-400' : 'text-emerald-600') : (isHigh ? (isDark ? 'text-red-400' : 'text-red-500') : (isDark ? 'text-amber-400' : 'text-amber-600'))}`} />
        <p className={`text-xs font-semibold flex-1 min-w-0 ${tp}`}>{alert.title}</p>
        {addressed ? (
          <span className={`text-[9px] font-medium px-1.5 py-0.5 rounded-full whitespace-nowrap flex items-center gap-1 ${isDark ? 'text-emerald-400 bg-emerald-400/10' : 'text-emerald-600 bg-emerald-600/10'}`}>
            <CheckCircle className="w-2.5 h-2.5" /> Actioned · Priya owns
          </span>
        ) : escalated ? (
          <span className={`text-[9px] font-medium px-1.5 py-0.5 rounded-full whitespace-nowrap flex items-center gap-1 ${isDark ? 'text-indigo-400 bg-indigo-400/10' : 'text-indigo-600 bg-indigo-600/10'}`}>
            <ArrowUpRight className="w-2.5 h-2.5" /> Escalated
          </span>
        ) : (
          <span className={`text-[9px] font-medium px-1.5 py-0.5 rounded-full whitespace-nowrap ${sevColor}`}>
            {isHigh ? 'High' : 'Medium'}
          </span>
        )}
      </div>
      <p className={`text-[10px] mt-1.5 ml-5.5 leading-relaxed ${ts}`}>{alert.description}</p>
      <div className="flex items-center gap-1.5 mt-2 ml-5.5">
        <button onClick={() => setExpanded(!expanded)} className={`flex items-center gap-1 px-2 py-0.5 rounded-md text-[10px] font-medium border ${border} ${ts} ${hoverBg}`} data-testid={`mkt-alert-expand-${alert.id}`}>
          {expanded ? <ChevronUp className="w-2.5 h-2.5" /> : <ChevronDown className="w-2.5 h-2.5" />}
          {expanded ? 'Less' : 'Details'}
        </button>
        {!addressed && (
          <button
            onClick={() => onDeepDive(alert)}
            className={`flex items-center gap-1 px-2.5 py-0.5 rounded-md text-[10px] font-medium transition-colors ${isDark ? 'bg-indigo-500/10 text-indigo-400 hover:bg-indigo-500/20' : 'bg-indigo-500/10 text-indigo-600 hover:bg-indigo-500/15'}`}
            data-testid={`mkt-alert-dive-${alert.id}`}
          >
            <Search className="w-2.5 h-2.5" /> Deep Dive
          </button>
        )}
        {!escalated && !addressed && (
          <button
            onClick={() => onEscalate(alert.id)}
            className={`flex items-center gap-1 px-2.5 py-0.5 rounded-md text-[10px] font-medium transition-colors ${isDark ? 'bg-rose-500/10 text-rose-400 hover:bg-rose-500/20' : 'bg-rose-500/10 text-rose-600 hover:bg-rose-500/15'}`}
            data-testid={`mkt-alert-escalate-${alert.id}`}
          >
            <ArrowUpRight className="w-2.5 h-2.5" /> Escalate to Priya
          </button>
        )}
      </div>
      {expanded && (
        <div className={`mt-2 ml-5.5 p-2.5 rounded-md ${isDark ? 'bg-white/[0.03]' : 'bg-black/[0.02]'}`}>
          <div className="flex flex-col gap-1.5">
            {alert.actions.map((a, i) => {
              const st = ACTION_STATUS[a.status] || ACTION_STATUS['exploring'];
              const Icon = st.icon;
              return (
                <div key={i} className="flex items-center gap-2">
                  <Icon className={`w-3 h-3 flex-shrink-0 ${isDark ? st.dark : st.light}`} />
                  <span className={`text-[10px] ${tp}`}>{a.label}</span>
                  <span className={`text-[9px] ${ts}`}>— {AGENT_NAMES[a.agent]}</span>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

/* ── Campaign Card ── */
const CampaignCard = ({ campaign, isDark, onDeepDive }) => {
  const [expanded, setExpanded] = useState(false);
  const tp = isDark ? 'text-white' : 'text-slate-800';
  const ts = isDark ? 'text-white/50' : 'text-slate-500';
  const cardBg = isDark ? 'bg-[#0d1b2a]' : 'bg-[#f0ece4]';
  const border = isDark ? 'border-white/[0.06]' : 'border-black/[0.06]';
  const status = CAMPAIGN_STATUS[campaign.status];
  const thumb = AGENT_THUMBS[campaign.agent];
  const pct = campaign.budget.total > 0 ? Math.round((campaign.budget.spent / campaign.budget.total) * 100) : 0;

  return (
    <div className={`${cardBg} border ${border} rounded-lg px-3 py-2.5`} data-testid={`mkt-campaign-${campaign.id}`}>
      <div className="flex items-center gap-2">
        {thumb && <img src={thumb} alt="" className="w-5 h-5 rounded-full object-cover flex-shrink-0" />}
        <p className={`text-xs font-semibold flex-1 min-w-0 truncate ${tp}`}>{campaign.name}</p>
        <span className={`text-[9px] font-medium px-1.5 py-0.5 rounded-full whitespace-nowrap flex items-center gap-1 ${isDark ? status.dark : status.light}`}>
          {campaign.status === 'live' && <Radio className="w-2.5 h-2.5" />}
          {status.label}
        </span>
      </div>

      {/* Budget bar */}
      <div className="mt-2 ml-7">
        <div className="flex items-center justify-between mb-1">
          <span className={`text-[10px] ${ts}`}>Budget</span>
          <span className={`text-[10px] font-medium ${tp}`}>${(campaign.budget.spent / 1000).toFixed(1)}K / ${(campaign.budget.total / 1000).toFixed(1)}K</span>
        </div>
        <div className={`w-full h-1 rounded-full ${isDark ? 'bg-white/[0.06]' : 'bg-black/[0.06]'}`}>
          <div className="h-full rounded-full bg-gradient-to-r from-indigo-500 to-purple-500" style={{ width: `${pct}%` }} />
        </div>
      </div>

      {/* Metrics */}
      <div className="flex flex-wrap gap-x-3 gap-y-1 mt-2 ml-7">
        {campaign.metrics.map((m, i) => (
          <div key={i} className="flex items-center gap-1">
            <span className={`text-[10px] ${ts}`}>{m.label}:</span>
            <span className={`text-[10px] font-medium ${m.alert ? (isDark ? 'text-amber-400' : 'text-amber-600') : tp}`}>{m.value}</span>
            {m.alert && m.target && <span className={`text-[9px] ${ts}`}>(target: {m.target})</span>}
          </div>
        ))}
      </div>

      <div className="flex items-center gap-3 mt-1.5 ml-7">
        <button
          onClick={() => setExpanded(!expanded)}
          className={`text-[10px] font-medium ${isDark ? 'text-indigo-400 hover:text-indigo-300' : 'text-indigo-600 hover:text-indigo-700'}`}
          data-testid={`mkt-campaign-toggle-${campaign.id}`}
        >
          {expanded ? 'Less' : 'More'}
        </button>
        <button
          onClick={() => onDeepDive && onDeepDive(campaign.id)}
          className={`flex items-center gap-1 text-[10px] font-medium ${isDark ? 'text-indigo-400 hover:text-indigo-300' : 'text-indigo-600 hover:text-indigo-700'}`}
          data-testid={`mkt-campaign-deep-dive-${campaign.id}`}
        >
          <Search className="w-3 h-3" /> Deep Dive
        </button>
      </div>

      {expanded && (
        <p className={`mt-1 ml-7 text-[11px] leading-relaxed ${ts}`}>{campaign.description}</p>
      )}
    </div>
  );
};

/* ── Decision Card ── */
const DecisionCard = ({ item, isDark, onApprove, onDeepDive, approved }) => {
  const tp = isDark ? 'text-white' : 'text-slate-800';
  const ts = isDark ? 'text-white/50' : 'text-slate-500';
  const border = isDark ? 'border-white/[0.06]' : 'border-black/[0.06]';
  const cardBg = approved
    ? (isDark ? 'bg-emerald-950/15' : 'bg-emerald-50/50')
    : (isDark ? 'bg-[#0d1b2a]' : 'bg-[#f0ece4]');
  const thumb = AGENT_THUMBS[item.agent];

  return (
    <div className={`${cardBg} border ${approved ? (isDark ? 'border-emerald-500/15' : 'border-emerald-200/60') : border} rounded-lg px-3 py-2.5`} data-testid={`mkt-decision-${item.id}`}>
      <div className="flex items-center gap-2">
        <Shield className={`w-3.5 h-3.5 flex-shrink-0 ${isDark ? 'text-indigo-400' : 'text-indigo-600'}`} />
        <p className={`text-xs font-semibold flex-1 min-w-0 ${tp}`}>{item.title}</p>
        {item.urgency === 'high' && !approved && (
          <span className={`text-[9px] font-medium px-1.5 py-0.5 rounded-full ${isDark ? 'text-red-400 bg-red-400/10' : 'text-red-600 bg-red-600/10'}`}>Urgent</span>
        )}
        {approved && (
          <span className={`text-[9px] font-medium px-1.5 py-0.5 rounded-full flex items-center gap-1 ${isDark ? 'text-emerald-400 bg-emerald-400/10' : 'text-emerald-600 bg-emerald-600/10'}`}>
            <CheckCircle className="w-2.5 h-2.5" /> Approved · Priya notified
          </span>
        )}
      </div>
      <p className={`text-[10px] mt-1 ml-5.5 ${ts}`}>{item.detail}</p>
      <div className="flex items-center gap-1.5 mt-2 ml-5.5">
        {thumb && <img src={thumb} alt="" className="w-3.5 h-3.5 rounded-full object-cover" />}
        <span className={`text-[9px] ${ts}`}>{AGENT_NAMES[item.agent]}</span>
        <div className="flex-1" />
        {!approved && (
          <>
            <button
              onClick={() => onDeepDive && onDeepDive(item.id)}
              className={`flex items-center gap-1 px-2 py-0.5 rounded-md text-[10px] font-medium ${isDark ? 'bg-indigo-500/10 text-indigo-400 hover:bg-indigo-500/20' : 'bg-indigo-500/10 text-indigo-600 hover:bg-indigo-500/15'} transition-colors`}
              data-testid={`mkt-decision-deep-dive-${item.id}`}
            >
              <Search className="w-2.5 h-2.5" /> Deep Dive
            </button>
            <button
              onClick={() => onApprove(item.id)}
              className={`flex items-center gap-1 px-2 py-0.5 rounded-md text-[10px] font-medium ${isDark ? 'bg-emerald-500/10 text-emerald-400 hover:bg-emerald-500/20' : 'bg-emerald-500/10 text-emerald-600 hover:bg-emerald-500/15'} transition-colors`}
              data-testid={`mkt-approve-${item.id}`}
            >
              <CheckCircle className="w-2.5 h-2.5" /> Approve
            </button>
          </>
        )}
      </div>
    </div>
  );
};

/* ── Activity Item ── */
const ActivityItem = ({ item, isDark }) => {
  const tp = isDark ? 'text-white' : 'text-slate-800';
  const ts = isDark ? 'text-white/50' : 'text-slate-500';
  const thumb = AGENT_THUMBS[item.agent];
  const typeDot = item.type === 'alert' ? 'bg-red-400' : item.type === 'insight' ? 'bg-indigo-400' : item.type === 'approval' ? 'bg-emerald-400' : 'bg-blue-400';

  return (
    <div className="flex items-start gap-2 py-1.5" data-testid={`mkt-activity-${item.id}`}>
      {thumb && <img src={thumb} alt="" className="w-5 h-5 rounded-full object-cover flex-shrink-0 mt-0.5" />}
      <div className="flex-1 min-w-0">
        <p className={`text-[11px] leading-relaxed ${tp}`}>
          <span className="font-semibold">{AGENT_NAMES[item.agent]}</span> {item.action}
        </p>
        <p className={`text-[9px] mt-0.5 ${ts}`}>{item.time}</p>
      </div>
      <div className={`w-1.5 h-1.5 rounded-full ${typeDot} flex-shrink-0 mt-1.5`} />
    </div>
  );
};

/* ── Channel Bar ── */
const ChannelBar = ({ channel, isDark }) => {
  const tp = isDark ? 'text-white' : 'text-slate-800';
  const ts = isDark ? 'text-white/50' : 'text-slate-500';
  const barBg = isDark ? 'bg-white/[0.06]' : 'bg-black/[0.06]';
  const barColor = channel.alert ? 'bg-gradient-to-r from-red-500 to-amber-500' : 'bg-gradient-to-r from-indigo-500 to-purple-500';

  return (
    <div className="flex items-center gap-2" data-testid={`mkt-channel-${channel.name.replace(/\s+/g, '-').toLowerCase()}`}>
      <span className={`text-[10px] w-24 truncate ${tp}`}>{channel.name}</span>
      <div className={`flex-1 h-1.5 rounded-full ${barBg}`}>
        <div className={`h-full rounded-full ${barColor} transition-all duration-500`} style={{ width: `${channel.performance}%` }} />
      </div>
      <span className={`text-[10px] font-medium w-8 text-right ${tp}`}>{channel.leads}</span>
      <span className={`text-[9px] w-10 text-right ${channel.alert ? (isDark ? 'text-red-400' : 'text-red-500') : (channel.trend.startsWith('+') ? (isDark ? 'text-emerald-400' : 'text-emerald-600') : (isDark ? 'text-red-400' : 'text-red-500'))}`}>
        {channel.trend}
      </span>
    </div>
  );
};

/* ── Chat Message ── */
const ChatMessage = ({ msg, isDark, onChipClick, chipsDisabled }) => {
  const tp = isDark ? 'text-white' : 'text-slate-800';
  const ts = isDark ? 'text-white/50' : 'text-slate-500';
  const border = isDark ? 'border-white/[0.06]' : 'border-black/[0.06]';

  if (msg.role === 'user') {
    return (
      <div className="flex justify-end" data-testid="mkt-chat-msg-user">
        <div className={`max-w-[85%] px-5 py-3.5 rounded-2xl rounded-br-md text-[14px] leading-[1.8] ${isDark ? 'bg-indigo-500/20 text-white' : 'bg-indigo-100 border border-indigo-200/80 text-slate-800'}`}>
          {msg.text}
        </div>
      </div>
    );
  }

  if (msg.role === 'thinking') {
    return (
      <div className="flex gap-4 items-center" data-testid="mkt-chat-thinking">
        <img src={AGENT_THUMBS.marsi} alt="" className="w-9 h-9 rounded-full object-cover flex-shrink-0" />
        <div className="flex items-center gap-2.5">
          <span className={`text-[13px] italic ${isDark ? 'text-white/40' : 'text-slate-400'}`}>Marsi is thinking</span>
          <span className="flex gap-1">
            <span className={`w-1 h-1 rounded-full ${isDark ? 'bg-white/35' : 'bg-slate-400'} animate-bounce`} style={{ animationDelay: '0ms' }} />
            <span className={`w-1 h-1 rounded-full ${isDark ? 'bg-white/35' : 'bg-slate-400'} animate-bounce`} style={{ animationDelay: '150ms' }} />
            <span className={`w-1 h-1 rounded-full ${isDark ? 'bg-white/35' : 'bg-slate-400'} animate-bounce`} style={{ animationDelay: '300ms' }} />
          </span>
        </div>
      </div>
    );
  }

  if (msg.role === 'complete-pill') {
    return (
      <div className="flex justify-center" data-testid="mkt-chat-complete">
        <div className={`flex items-center gap-2 px-3.5 py-1.5 rounded-full text-[11px] font-medium ${isDark ? 'bg-emerald-400/10 text-emerald-400 border border-emerald-400/15' : 'bg-emerald-500/10 text-emerald-700 border border-emerald-500/15'}`}>
          <Check className="w-3 h-3" /> Conversation complete
        </div>
      </div>
    );
  }

  return (
    <div className="flex gap-4" data-testid="mkt-chat-msg-agent">
      <img src={AGENT_THUMBS.marsi} alt="" className="w-9 h-9 rounded-full object-cover flex-shrink-0 mt-0.5" />
      <div className="flex-1 min-w-0 space-y-4">
        {msg.text && (
          <div className={`text-[14px] leading-[1.8] whitespace-pre-line ${tp}`}>{msg.text}</div>
        )}

        {/* Chips (CEO quick-reply options) — hidden once a chip is picked */}
        {msg.chips && !chipsDisabled && (
          <div className="flex flex-wrap gap-2 pt-3" data-testid="mkt-chips">
            {msg.chips.map((c) => (
              <button
                key={c.id}
                onClick={() => onChipClick && onChipClick(c)}
                className={`px-3.5 py-1.5 rounded-full text-[12px] font-medium border transition-all ${isDark ? 'bg-white/[0.04] border-white/[0.08] text-white/80 hover:bg-indigo-500/15 hover:border-indigo-500/30 hover:text-white' : 'bg-white border-black/[0.08] text-slate-700 hover:bg-indigo-500/10 hover:border-indigo-500/30 hover:text-slate-900'}`}
                data-testid={`mkt-chip-${c.id}`}
              >
                {c.label}
              </button>
            ))}
          </div>
        )}

        {/* Execution progress — staggered check list */}
        {msg.executionSteps && (
          <div className={`rounded-xl px-4 py-3 ${isDark ? 'bg-indigo-500/8 border border-indigo-500/10' : 'bg-indigo-50 border border-indigo-200/40'}`} data-testid="mkt-exec-progress">
            <div className="space-y-2">
              {msg.executionSteps.map((step, i) => (
                <div key={i} className="flex items-center gap-2.5" style={{ animation: `mktFade 0.35s ease-out ${i * 0.25}s both` }}>
                  <Check className={`w-3.5 h-3.5 flex-shrink-0 ${isDark ? 'text-emerald-400' : 'text-emerald-600'}`} />
                  <span className={`text-[13px] ${isDark ? 'text-white/75' : 'text-slate-700'}`}>{step}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Priya task artifact */}
        {msg.priyaTask && (
          <div className={`rounded-xl border overflow-hidden ${isDark ? 'bg-white/[0.02] border-white/[0.08]' : 'bg-white/80 border-black/[0.06]'}`} data-testid="mkt-priya-task">
            <div className={`px-4 py-2.5 border-b ${border} flex items-center gap-2 ${isDark ? 'bg-gradient-to-r from-purple-500/8 to-indigo-500/8' : 'bg-gradient-to-r from-purple-50 to-indigo-50'}`}>
              <ClipboardList className={`w-3.5 h-3.5 ${isDark ? 'text-purple-400' : 'text-purple-600'}`} />
              <span className={`text-[10px] font-semibold uppercase tracking-wider ${isDark ? 'text-purple-400' : 'text-purple-600'}`}>Task routed to Priya</span>
            </div>
            <div className="px-4 py-3">
              <p className={`text-[13px] font-semibold leading-snug ${tp}`}>{msg.priyaTask.title}</p>
              <p className={`text-[12px] leading-relaxed mt-1 ${ts}`}>{msg.priyaTask.context}</p>
              <div className={`flex items-center gap-3 mt-2.5 pt-2.5 border-t ${border}`}>
                <div className="flex items-center gap-1.5">
                  <img src={AGENT_THUMBS[msg.priyaTask.agent]} alt="" className="w-4 h-4 rounded-full object-cover" />
                  <span className={`text-[11px] ${ts}`}>{AGENT_NAMES[msg.priyaTask.agent] || msg.priyaTask.agent}</span>
                </div>
                <div className="flex items-center gap-1">
                  <Clock className={`w-3 h-3 ${ts}`} />
                  <span className={`text-[11px] ${ts}`}>{msg.priyaTask.dueDate}</span>
                </div>
                <span className={`ml-auto text-[10px] font-medium px-2 py-0.5 rounded-full ${isDark ? 'bg-amber-400/10 text-amber-400' : 'bg-amber-500/10 text-amber-600'}`}>Pending with Priya</span>
              </div>
            </div>
          </div>
        )}

        {/* Closure — italic line */}
        {msg.closure && (
          <div className={`text-[13px] italic leading-relaxed ${isDark ? 'text-white/70' : 'text-slate-600'}`}>
            {msg.closure}
          </div>
        )}

        {/* Legacy actions support (still used by alert dive / decision approvals) */}
        {msg.actions && (
          <div className="flex flex-col gap-2">
            {msg.actions.map((a, i) => {
              const st = ACTION_STATUS[a.status] || ACTION_STATUS['exploring'];
              const Icon = st.icon;
              const thumb = AGENT_THUMBS[a.agent];
              return (
                <div key={i} className={`flex items-start gap-2.5 px-3 py-2 rounded-lg border ${border} ${isDark ? 'bg-white/[0.02]' : 'bg-black/[0.02]'}`}>
                  <Icon className={`w-3.5 h-3.5 flex-shrink-0 mt-0.5 ${isDark ? st.dark : st.light}`} />
                  <div className="flex-1 min-w-0">
                    <p className={`text-[13px] leading-relaxed ${tp}`}>{a.label}</p>
                    <div className="flex items-center gap-1.5 mt-1">
                      {thumb && <img src={thumb} alt="" className="w-3.5 h-3.5 rounded-full object-cover" />}
                      <span className={`text-[11px] ${ts}`}>{AGENT_NAMES[a.agent]}</span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {msg.timeline && (
          <div className={`flex items-start gap-2 px-3 py-2 rounded-lg ${isDark ? 'bg-indigo-500/5 border border-indigo-500/10' : 'bg-indigo-500/5 border border-indigo-500/10'}`}>
            <Clock className={`w-3.5 h-3.5 flex-shrink-0 mt-0.5 ${isDark ? 'text-indigo-400' : 'text-indigo-600'}`} />
            <p className={`text-[12px] leading-relaxed ${isDark ? 'text-indigo-300' : 'text-indigo-700'}`}>{msg.timeline}</p>
          </div>
        )}
      </div>
    </div>
  );
};

/* ════════════════════════════════════════ */
/* ── Main Component ── */
/* ════════════════════════════════════════ */

/* Normalize a deep-dive turn into a chat message.
   Turns may contain: text, chips, executionSteps, priyaTask, closure. */
const normalizeTurn = (turn) => ({
  role: 'agent',
  text: turn.text || null,
  chips: turn.chips || null,
  executionSteps: turn.executionSteps || null,
  priyaTask: turn.priyaTask || null,
  closure: turn.closure || null,
});

const V4MarketingDept = ({ isDark, onWorkflowToggle, workflowPanelOpen, onToast }) => {
  const [message, setMessage] = useState('');
  useRegisterChatInput(setMessage);
  const [chatMode, setChatMode] = useState(false);
  const [chatMessages, setChatMessages] = useState([]);
  const [escalatedAlerts, setEscalatedAlerts] = useState(new Set());
  const [addressedAlerts, setAddressedAlerts] = useState(new Set());
  const [approvedDecisions, setApprovedDecisions] = useState(new Set());
  const [deepDiveBranch, setDeepDiveBranch] = useState(null); // 'a' | 'b' | 'c' | null
  const [deepDiveTurn, setDeepDiveTurn] = useState(0);
  const messagesEndRef = useRef(null);
  const deepDiveTimersRef = useRef([]);

  const data = MARKETING_DEPT_DATA;
  const tp = isDark ? 'text-white' : 'text-slate-800';
  const ts = isDark ? 'text-white/50' : 'text-slate-500';
  const border = isDark ? 'border-white/[0.06]' : 'border-black/[0.06]';
  const solidBg = isDark ? 'bg-[#0a1628]' : 'bg-[#eee8df]';
  const inputBorder = isDark ? 'border-white/[0.08]' : 'border-black/[0.08]';
  const hoverBg = isDark ? 'hover:bg-white/[0.06]' : 'hover:bg-black/[0.04]';

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [chatMessages]);

  const pushMessage = (msg) => {
    setChatMessages((prev) => [...prev, msg]);
  };

  const addDeepDiveTimer = (cb, delay) => {
    const id = setTimeout(cb, scaleDelay(delay));
    deepDiveTimersRef.current.push(id);
  };

  // Clear any in-flight timers + reset chat state. Used by every entry point.
  const resetChat = () => {
    deepDiveTimersRef.current.forEach(clearTimeout);
    deepDiveTimersRef.current = [];
    setDeepDiveBranch(null);
    setDeepDiveTurn(0);
    setChatMessages([]);
  };

  const handleDeepDive = () => {
    resetChat();
    const opening = MARKETING_DEEP_DIVE.opening;
    // Seed with CEO opening bubble only
    setChatMessages([{ role: 'user', text: opening.ceo }]);
    setChatMode(true);

    // Thinking pause → Marsi's narrative + 3 branch chips
    addDeepDiveTimer(() => pushMessage({ role: 'thinking' }), 500);
    addDeepDiveTimer(() => {
      setChatMessages((prev) => {
        const withoutThinking = prev.filter((m) => m.role !== 'thinking');
        return [...withoutThinking, { role: 'agent', text: opening.marsi, chips: opening.chips }];
      });
    }, 2000);
  };

  const sendMessage = (text, responseData) => {
    const userMsg = { role: 'user', text };
    const agentMsg = {
      role: 'agent',
      text: responseData?.text || MARKETING_CHAT_RESPONSES.general.text,
      actions: responseData?.actions || null,
      timeline: responseData?.timeline || null,
    };
    setChatMessages(prev => [...prev, userMsg, agentMsg]);
    setChatMode(true);
  };

  const handleSend = () => {
    if (message.trim()) {
      sendMessage(message.trim(), null);
      setMessage('');
    }
  };

  // Disable chips on a previous agent message once user picks one
  const lockPreviousChips = () => {
    setChatMessages((prev) => prev.map((m) => (m.chips ? { ...m, chipsDisabled: true } : m)));
  };

  /* Append CEO acknowledgement + Marsi brief nod + "conversation complete" pill.
     Called after the final artifact (Priya task + closure) has been rendered. */
  const appendConversationClosure = () => {
    addDeepDiveTimer(() => pushMessage({ role: 'user', text: 'Perfect. Thanks, Marsi — keep me posted.' }), 2000);
    addDeepDiveTimer(() => pushMessage({ role: 'thinking' }), 2600);
    addDeepDiveTimer(() => {
      setChatMessages((prev) => {
        const withoutThinking = prev.filter((m) => m.role !== 'thinking');
        return [...withoutThinking, { role: 'agent', text: 'Will do. Talk soon.' }];
      });
    }, 3800);
    addDeepDiveTimer(() => pushMessage({ role: 'complete-pill' }), 4600);
  };

  const handleChipClick = (chip) => {
    // Lock the chips that were just displayed (visually hidden on next render)
    lockPreviousChips();

    // User bubble echoing the chip label
    pushMessage({ role: 'user', text: chip.label });

    // Opening-level chip: chip.id is 'a' | 'b' | 'c' — kick off the branch
    if (!deepDiveBranch) {
      const branchKey = chip.id; // 'a' | 'b' | 'c'
      const turns = MARKETING_DEEP_DIVE.branches[branchKey];
      if (!turns) return;

      setDeepDiveBranch(branchKey);
      setDeepDiveTurn(0);

      addDeepDiveTimer(() => pushMessage({ role: 'thinking' }), 500);
      addDeepDiveTimer(() => {
        const first = turns[0];
        setChatMessages((prev) => {
          const withoutThinking = prev.filter((m) => m.role !== 'thinking');
          return [...withoutThinking, normalizeTurn(first)];
        });
      }, 1800);
      return;
    }

    // Within-branch chip — advance through the top-right Deep Dive turns
    const turns = MARKETING_DEEP_DIVE.branches[deepDiveBranch];
    if (!turns) return;
    const nextIdx = deepDiveTurn + 1;
    setDeepDiveTurn(nextIdx);
    const nextTurn = turns[nextIdx];
    if (!nextTurn) return;

    const resolved = nextTurn.role === 'meta' && nextTurn.perChip ? nextTurn.perChip[chip.id] : nextTurn;
    if (!resolved) return;

    addDeepDiveTimer(() => pushMessage({ role: 'thinking' }), 400);
    addDeepDiveTimer(() => {
      setChatMessages((prev) => {
        const withoutThinking = prev.filter((m) => m.role !== 'thinking');
        return [...withoutThinking, normalizeTurn(resolved)];
      });
    }, 1600);

    // If the resolved turn is terminal (has a Priya task or closure, and no new chips), append closure
    if ((resolved.priyaTask || resolved.closure) && !resolved.chips) {
      addDeepDiveTimer(() => appendConversationClosure(), 1800);
    }
  };

  /* Auto-play a flat scripted conversation.
     Supports 'user', 'agent', 'complete-pill' turn roles.
     Agent turns show a "Marsi is thinking" indicator before rendering.
     User turns just appear after a short 1s pause (as if typed). */
  const autoPlayScript = (turns) => {
    const playNext = (idx) => {
      if (idx >= turns.length) return;
      const turn = turns[idx];

      if (turn.role === 'user') {
        addDeepDiveTimer(() => {
          pushMessage({ role: 'user', text: turn.text });
          playNext(idx + 1);
        }, 1400);
        return;
      }

      if (turn.role === 'complete-pill') {
        addDeepDiveTimer(() => {
          pushMessage({ role: 'complete-pill' });
          playNext(idx + 1);
        }, 700);
        return;
      }

      // Agent turn — thinking pause, then the message
      addDeepDiveTimer(() => pushMessage({ role: 'thinking' }), 500);
      const textPause = 2100 + (turn.text ? Math.min(turn.text.length * 3, 1200) : 0);
      addDeepDiveTimer(() => {
        setChatMessages((prev) => {
          const withoutThinking = prev.filter((m) => m.role !== 'thinking');
          return [
            ...withoutThinking,
            {
              role: 'agent',
              text: turn.text,
              executionSteps: turn.executionSteps || null,
              priyaTask: turn.priyaTask || null,
              closure: turn.closure || null,
            },
          ];
        });
        // Apply ripple effects (dashboard state updates) when this turn is the
        // actionable moment — approve linked decisions, mark linked alerts actioned.
        if (turn.ripple) {
          if (turn.ripple.approveDecision) {
            setApprovedDecisions((prev) => {
              const next = new Set(prev);
              next.add(turn.ripple.approveDecision);
              return next;
            });
          }
          if (turn.ripple.actionAlert) {
            setAddressedAlerts((prev) => {
              const next = new Set(prev);
              next.add(turn.ripple.actionAlert);
              return next;
            });
          }
        }
        playNext(idx + 1);
      }, textPause);
    };
    playNext(0);
  };

  // Cleanup deep dive timers on unmount
  useEffect(() => {
    return () => deepDiveTimersRef.current.forEach(clearTimeout);
  }, []);

  /* Alert Deep Dive — auto-play the per-alert scripted conversation.
     Falls back to a minimal single-message summary for alerts without scripts. */
  const handleAlertDive = (alert) => {
    resetChat();
    setChatMode(true);
    const script = alert?.id ? MARKETING_ALERT_DEEP_DIVES[alert.id] : null;
    if (script) {
      autoPlayScript(script);
    } else {
      // Fallback for campaign/generic entries passed as pseudo-alerts
      setChatMessages([
        { role: 'user', text: `Tell me more about "${alert.title}".` },
      ]);
      addDeepDiveTimer(() => pushMessage({ role: 'thinking' }), 500);
      addDeepDiveTimer(() => {
        setChatMessages((prev) => {
          const withoutThinking = prev.filter((m) => m.role !== 'thinking');
          return [...withoutThinking, { role: 'agent', text: alert.description || 'No additional context available.' }];
        });
      }, 2100);
    }
  };

  const handleEscalate = (alertId) => {
    setEscalatedAlerts(prev => new Set([...prev, alertId]));
    const alert = data.alerts.find(a => a.id === alertId);
    // Toast notification — don't open the chat panel.
    if (onToast) {
      onToast({
        type: 'handoff',
        title: 'Escalated to Priya',
        message: `Marsi notified Priya: "${alert.title}"`,
      });
    }
  };

  const handleApproveDecision = (decisionId) => {
    // Mark card as approved + fire a toast. Do NOT open the chat panel.
    setApprovedDecisions(prev => new Set([...prev, decisionId]));
    const decision = data.decisions.find(d => d.id === decisionId);
    if (onToast && decision) {
      onToast({
        type: 'success',
        title: 'Approved · Priya notified',
        message: `${decision.title} — Marsi is executing now.`,
      });
    }
  };

  const handleDecisionDeepDive = (decisionId) => {
    const script = MARKETING_DECISION_DEEP_DIVES[decisionId];
    if (!script) return;
    resetChat();
    setChatMode(true);
    autoPlayScript(script);
  };

  const handleCampaignDeepDive = (campaignId) => {
    const script = MARKETING_CAMPAIGN_DEEP_DIVES[campaignId];
    if (!script) return;
    resetChat();
    setChatMode(true);
    autoPlayScript(script);
  };

  const handleInput = (e) => {
    const textarea = e.target;
    textarea.style.height = 'auto';
    textarea.style.height = Math.min(textarea.scrollHeight, 200) + 'px';
    setMessage(e.target.value);
  };

  /* ── Chat input (shared) ── */
  const chatInput = (
    <div className="flex justify-center pb-4 px-4 flex-shrink-0">
      <div className="w-[80%]">
        <div className={`${solidBg} border ${inputBorder} rounded-2xl flex flex-col`}>
          <div className="px-4 pt-3 pb-2">
            <textarea
              value={message}
              onChange={handleInput}
              onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSend(); } }}
              placeholder="Message Marsi about Marketing..."
              rows={1}
              className={`w-full bg-transparent resize-none outline-none text-sm leading-5 ${tp} placeholder:${ts}`}
              style={{ height: 'auto', maxHeight: '200px', overflow: message.split('\n').length > 8 ? 'auto' : 'hidden' }}
              data-testid="mkt-chat-input"
            />
          </div>
          <div className="flex items-center justify-between px-3 pb-3">
            <div className="flex items-center gap-1">
              <div className={`flex items-center gap-1.5 px-2 py-1.5 rounded-lg ${isDark ? 'bg-white/[0.04]' : 'bg-black/[0.03]'}`}>
                <img src={AGENT_THUMBS.marsi} alt="" className="w-5 h-5 rounded-full object-cover" />
                <span className={`text-xs font-medium ${tp}`}>Marsi</span>
              </div>
              <button
                onClick={onWorkflowToggle}
                className={`flex items-center gap-1.5 px-2 py-1.5 rounded-lg transition-colors ${workflowPanelOpen ? (isDark ? 'bg-white/[0.08]' : 'bg-black/[0.06]') : hoverBg}`}
                data-testid="mkt-workflow-btn"
              >
                <Zap className={`w-3.5 h-3.5 ${tp}`} />
                <span className={`text-xs font-medium ${tp}`}>Workflows</span>
              </button>
              <button className={`p-1.5 rounded-lg transition-colors ${hoverBg}`} data-testid="mkt-attach-btn">
                <Paperclip className={`w-3.5 h-3.5 ${ts}`} />
              </button>
            </div>
            <div className="flex items-center gap-1.5">
              <button className={`w-8 h-8 rounded-xl flex items-center justify-center transition-colors ${hoverBg}`} data-testid="mkt-voice-btn">
                <Mic className={`w-4 h-4 ${ts}`} />
              </button>
              <button
                onClick={handleSend}
                className={`w-8 h-8 rounded-xl flex items-center justify-center transition-colors ${message.trim() ? 'bg-gradient-to-br from-indigo-500 to-purple-600' : (isDark ? 'bg-white/[0.06]' : 'bg-black/[0.04]')}`}
                data-testid="mkt-send-btn"
              >
                <Send className={`w-3.5 h-3.5 ${message.trim() ? 'text-white' : ts}`} />
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  /* ── Chat view ── */
  if (chatMode) {
    return (
      <div className="flex flex-col h-full" data-testid="mkt-dept-chat-view">
        <style>{`@keyframes mktFade { from { opacity: 0; transform: translateY(6px); } to { opacity: 1; transform: translateY(0); } }`}</style>
        <div className={`px-4 py-3 flex-shrink-0 border-b ${border}`}>
          <button
            onClick={() => setChatMode(false)}
            className={`flex items-center gap-2 px-2 py-1 rounded-lg transition-colors ${hoverBg}`}
            data-testid="mkt-back-btn"
          >
            <ArrowLeft className={`w-4 h-4 ${ts}`} />
            <span className={`text-sm font-medium ${tp}`}>Back to Marketing</span>
          </button>
        </div>

        <div className="flex-1 overflow-y-auto">
          <div className="flex justify-center">
            <div className="w-[80%] py-6">
              {chatMessages.map((msg, i) => (
                <div key={i} style={{ marginTop: i > 0 ? 32 : 0 }}>
                  <ChatMessage msg={msg} isDark={isDark} onChipClick={handleChipClick} chipsDisabled={msg.chipsDisabled} />
                </div>
              ))}
              <div ref={messagesEndRef} />
            </div>
          </div>
        </div>

        {chatInput}
      </div>
    );
  }

  /* ── Data view (default) ── */
  return (
    <div className="flex flex-col h-full" data-testid="mkt-dept-page">
      {/* Health Header */}
      <div className="px-5 pt-4 pb-3 flex-shrink-0">
        <div className="flex items-center gap-5">
          <ScoreRing score={data.health.score} size={68} strokeWidth={4} isDark={isDark} status={data.health.status} />
          <div className="flex-1">
            <div className="flex items-center gap-2">
              <Megaphone className={`w-4 h-4 ${isDark ? 'text-indigo-400' : 'text-indigo-600'}`} />
              <p className={`text-sm font-semibold ${tp}`}>Marketing</p>
              <div className="flex items-center gap-1">
                <TrendingUp className={`w-3 h-3 ${isDark ? 'text-emerald-400' : 'text-emerald-600'}`} />
                <span className={`text-[11px] ${isDark ? 'text-emerald-400' : 'text-emerald-600'}`}>{data.health.trend} pts this week</span>
              </div>
            </div>
            <p className={`text-[11px] mt-0.5 ${ts}`}>{data.health.summary}</p>
          </div>
          <button
            onClick={handleDeepDive}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[11px] font-medium transition-colors ${isDark ? 'bg-indigo-500/10 text-indigo-400 hover:bg-indigo-500/20' : 'bg-indigo-500/10 text-indigo-600 hover:bg-indigo-500/15'}`}
            data-testid="mkt-deep-dive-btn"
          >
            <Search className="w-3.5 h-3.5" /> Deep Dive
          </button>
        </div>
      </div>

      <div className={`border-t ${border}`} />

      {/* 3-column grid */}
      <div className="flex-1 min-h-0 overflow-hidden">
        <div className="grid grid-cols-3 gap-5 h-full px-5 py-4">

          {/* Column 1: Agents & Alerts */}
          <div className="flex flex-col min-h-0 overflow-y-auto pr-1">
            {/* Agents */}
            <div className="flex items-center gap-2 mb-3">
              <Bot className={`w-4 h-4 ${isDark ? 'text-indigo-400' : 'text-indigo-600'}`} />
              <p className={`text-xs font-semibold ${tp}`}>Marketing Agents</p>
              <span className={`text-[9px] font-medium px-1.5 py-0.5 rounded-full ${isDark ? 'bg-emerald-400/10 text-emerald-400' : 'bg-emerald-600/10 text-emerald-600'}`}>
                {data.agents.filter(a => a.status === 'active').length} active
              </span>
            </div>
            <div className="flex flex-col gap-2 mb-4">
              {data.agents.map(a => <AgentCard key={a.id} agent={a} isDark={isDark} />)}
            </div>

            {/* Alerts */}
            <div className="flex items-center gap-2 mb-3">
              <AlertTriangle className={`w-4 h-4 ${isDark ? 'text-red-400' : 'text-red-500'}`} />
              <p className={`text-xs font-semibold ${tp}`}>Alerts</p>
              <span className={`text-[9px] font-medium px-1.5 py-0.5 rounded-full ${isDark ? 'bg-red-400/10 text-red-400' : 'bg-red-600/10 text-red-600'}`}>{data.alerts.length}</span>
            </div>
            <div className="flex flex-col gap-2">
              {data.alerts.map(a => <AlertCard key={a.id} alert={a} isDark={isDark} onDeepDive={handleAlertDive} onEscalate={handleEscalate} escalated={escalatedAlerts.has(a.id)} addressed={addressedAlerts.has(a.id)} />)}
            </div>
          </div>

          {/* Column 2: Campaigns & Approvals */}
          <div className="flex flex-col min-h-0 overflow-y-auto pr-1">
            {/* Campaigns */}
            <div className="flex items-center gap-2 mb-3">
              <Target className={`w-4 h-4 ${isDark ? 'text-indigo-400' : 'text-indigo-600'}`} />
              <p className={`text-xs font-semibold ${tp}`}>Active Campaigns</p>
              <span className={`text-[9px] font-medium px-1.5 py-0.5 rounded-full ${isDark ? 'bg-indigo-400/10 text-indigo-400' : 'bg-indigo-600/10 text-indigo-600'}`}>{data.campaigns.length}</span>
            </div>
            <div className="flex flex-col gap-2 mb-4">
              {data.campaigns.map(c => <CampaignCard key={c.id} campaign={c} isDark={isDark} onDeepDive={handleCampaignDeepDive} />)}
            </div>

            {/* Needs Your Decision */}
            <div className="flex items-center gap-2 mb-3">
              <Shield className={`w-4 h-4 ${isDark ? 'text-indigo-400' : 'text-indigo-600'}`} />
              <p className={`text-xs font-semibold ${tp}`}>Needs Your Decision</p>
              <span className={`text-[9px] font-medium px-1.5 py-0.5 rounded-full ${isDark ? 'bg-indigo-400/10 text-indigo-400' : 'bg-indigo-600/10 text-indigo-600'}`}>
                {data.decisions.filter(d => !approvedDecisions.has(d.id)).length}
              </span>
            </div>
            <div className="flex flex-col gap-2">
              {data.decisions.map(d => <DecisionCard key={d.id} item={d} isDark={isDark} onApprove={handleApproveDecision} onDeepDive={handleDecisionDeepDive} approved={approvedDecisions.has(d.id)} />)}
            </div>
          </div>

          {/* Column 3: Metrics, Channels, Activity */}
          <div className="flex flex-col min-h-0 overflow-y-auto pr-1">
            {/* Key Metrics */}
            <div className="flex items-center gap-2 mb-3">
              <BarChart3 className={`w-4 h-4 ${isDark ? 'text-indigo-400' : 'text-indigo-600'}`} />
              <p className={`text-xs font-semibold ${tp}`}>Key Metrics</p>
            </div>
            <div className="grid grid-cols-2 gap-2 mb-4">
              {data.metrics.map((m, i) => (
                <div key={i} className={`px-2.5 py-2 rounded-lg border ${m.alert ? (isDark ? 'border-red-500/15 bg-red-500/5' : 'border-red-200/50 bg-red-50/50') : (isDark ? 'border-white/[0.06] bg-[#0d1b2a]' : 'border-black/[0.06] bg-[#f0ece4]')}`} data-testid={`mkt-metric-${i}`}>
                  <p className={`text-[9px] ${ts}`}>{m.label}</p>
                  <div className="flex items-baseline gap-1.5">
                    <span className={`text-base font-bold ${m.alert ? (isDark ? 'text-red-400' : 'text-red-600') : tp}`}>{m.value}</span>
                    <span className={`text-[10px] font-medium ${m.trendUp ? (isDark ? 'text-emerald-400' : 'text-emerald-600') : (isDark ? 'text-red-400' : 'text-red-500')}`}>
                      {m.trend}
                    </span>
                  </div>
                </div>
              ))}
            </div>

            {/* Channel Performance */}
            <div className="flex items-center gap-2 mb-3">
              <BarChart3 className={`w-4 h-4 ${isDark ? 'text-indigo-400' : 'text-indigo-600'}`} />
              <p className={`text-xs font-semibold ${tp}`}>Channel Performance</p>
              <span className={`text-[9px] ${ts}`}>leads</span>
            </div>
            <div className={`px-3 py-2.5 rounded-lg border ${isDark ? 'border-white/[0.06] bg-[#0d1b2a]' : 'border-black/[0.06] bg-[#f0ece4]'} flex flex-col gap-2 mb-4`}>
              {data.channels.map(c => <ChannelBar key={c.name} channel={c} isDark={isDark} />)}
            </div>

            {/* Recent Activity */}
            <div className="flex items-center gap-2 mb-3">
              <Zap className={`w-4 h-4 ${isDark ? 'text-indigo-400' : 'text-indigo-600'}`} />
              <p className={`text-xs font-semibold ${tp}`}>Agent Activity</p>
              <span className={`text-[9px] font-medium px-1.5 py-0.5 rounded-full ${isDark ? 'bg-indigo-400/10 text-indigo-400' : 'bg-indigo-600/10 text-indigo-600'}`}>Live</span>
            </div>
            <div className={`flex flex-col divide-y ${isDark ? 'divide-white/[0.04]' : 'divide-black/[0.04]'}`}>
              {data.recentActivity.map(item => <ActivityItem key={item.id} item={item} isDark={isDark} />)}
            </div>
          </div>
        </div>
      </div>

      {/* Chat input */}
      {chatInput}
    </div>
  );
};

export default V4MarketingDept;
